import { useState, useCallback, useEffect } from 'react';
import { 
  Search, Filter, ChevronDown, ChevronUp, X,
  MapPin, Building2, Shield, Eye, EyeOff, FileText,
  Users, Clock, CheckCircle, AlertTriangle, RefreshCw
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configurazioni
const ADMIN_STATUS_CONFIG = {
  registered: { label: 'Registrato', color: 'bg-gray-100 text-gray-700', icon: Clock },
  docs_pending: { label: 'Docs Mancanti', color: 'bg-amber-100 text-amber-700', icon: FileText },
  under_review: { label: 'In Revisione', color: 'bg-blue-100 text-blue-700', icon: Eye },
  contact_pending: { label: 'Da Contattare', color: 'bg-purple-100 text-purple-700', icon: Users },
  pre_verified: { label: 'Pre-Verificato', color: 'bg-cyan-100 text-cyan-700', icon: CheckCircle },
  approved: { label: 'Approvato', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rifiutato', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  suspended: { label: 'Sospeso', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle }
};

const RISK_CONFIG = {
  low: { label: 'Basso', color: 'bg-green-50 text-green-700' },
  medium: { label: 'Medio', color: 'bg-amber-50 text-amber-700' },
  high: { label: 'Alto', color: 'bg-red-50 text-red-700' }
};

const SOURCE_CONFIG = {
  provider_portal: { label: 'Portale', color: 'bg-blue-50 text-blue-700' },
  admin_manual: { label: 'Admin', color: 'bg-purple-50 text-purple-700' },
  import_curated: { label: 'Import', color: 'bg-green-50 text-green-700' },
  google_places: { label: 'Google', color: 'bg-amber-50 text-amber-700' }
};

const ONBOARDING_METHOD_CONFIG = {
  chat: { label: 'Chat Guidata', color: 'bg-indigo-50 text-indigo-700', icon: '💬' },
  form: { label: 'Form Classico', color: 'bg-slate-50 text-slate-700', icon: '📝' },
  skipped: { label: 'Saltato', color: 'bg-gray-50 text-gray-600', icon: '⏭️' },
  unknown: { label: 'N/D', color: 'bg-gray-50 text-gray-500', icon: '?' }
};

const CATEGORIES = [
  { value: 'accommodation', label: 'Alloggi' },
  { value: 'restaurant', label: 'Ristoranti' },
  { value: 'winery', label: 'Cantine' },
  { value: 'guide', label: 'Guide' },
  { value: 'transport', label: 'Trasporti/NCC' },
  { value: 'babysitting', label: 'Babysitter', sensitive: true },
  { value: 'wellness', label: 'Wellness' },
  { value: 'experience', label: 'Esperienze' },
  { value: 'rental', label: 'Noleggi' },
  { value: 'museum', label: 'Musei' }
];

const MACRO_AREAS = [
  'Valle Umbra', 'Perugino', 'Trasimeno', 'Ternano', 'Spoletino',
  'Tuderte', 'Orvietano', 'Altotevere', 'Appennino', 'Valnerina'
];

/**
 * ProviderSearch - Ricerca avanzata provider con filtri a due livelli
 * 
 * Filtri primari (sempre visibili): Testo, Categoria, Stato
 * Filtri avanzati (collassabili): Rischio, Source, Area, Visibilità, Docs, etc.
 */
export function ProviderSearch({ onSelectProvider, initialFilter }) {
  // State filtri primari
  const [searchText, setSearchText] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  // State filtri avanzati
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [filterRisk, setFilterRisk] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterMacroArea, setFilterMacroArea] = useState('');
  const [filterOnboardingMethod, setFilterOnboardingMethod] = useState('');
  const [showChiaraOnly, setShowChiaraOnly] = useState(false);
  const [showSensitiveOnly, setShowSensitiveOnly] = useState(false);
  const [showMissingDocs, setShowMissingDocs] = useState(false);
  const [showManualVetting, setShowManualVetting] = useState(false);
  
  // Results state
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  // Apply initial filter
  useEffect(() => {
    if (initialFilter) {
      switch (initialFilter) {
        case 'urgent':
        case 'sensitive':
          setShowSensitiveOnly(true);
          setFilterStatus('under_review');
          setShowAdvanced(true);
          break;
        case 'review':
          setFilterStatus('under_review');
          break;
        case 'fasttrack':
          setFilterStatus('under_review');
          setFilterRisk('low');
          setShowAdvanced(true);
          break;
        case 'contact':
          setFilterStatus('contact_pending');
          break;
        default:
          break;
      }
    }
  }, [initialFilter]);

  const searchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const body = {
        search_text: searchText || null,
        type: filterCategory || null,
        admin_status: filterStatus ? [filterStatus] : null,
        risk_level: filterRisk || null,
        source: filterSource || null,
        macro_area: filterMacroArea || null,
        onboarding_method: filterOnboardingMethod || null,
        visible_to_chiara: showChiaraOnly ? true : null,
        sensitive_only: showSensitiveOnly || null,
        missing_docs: showMissingDocs || null,
        manual_vetting_required: showManualVetting || null,
        limit: 50
      };
      
      const res = await fetch(`${API}/admin/providers/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      const data = await res.json();
      setProviders(data.providers || []);
      setTotalCount(data.total || 0);
    } catch (err) {
      console.error('Search failed:', err);
      toast.error('Errore nella ricerca');
    } finally {
      setLoading(false);
    }
  }, [searchText, filterCategory, filterStatus, filterRisk, filterSource, filterMacroArea, filterOnboardingMethod, showChiaraOnly, showSensitiveOnly, showMissingDocs, showManualVetting]);

  // Search on mount and filter changes
  useEffect(() => {
    searchProviders();
  }, [searchProviders]);

  const clearFilters = () => {
    setSearchText('');
    setFilterCategory('');
    setFilterStatus('');
    setFilterRisk('');
    setFilterSource('');
    setFilterMacroArea('');
    setFilterOnboardingMethod('');
    setShowChiaraOnly(false);
    setShowSensitiveOnly(false);
    setShowMissingDocs(false);
    setShowManualVetting(false);
  };

  const activeFiltersCount = [
    filterRisk, filterSource, filterMacroArea, filterOnboardingMethod,
    showChiaraOnly, showSensitiveOnly, showMissingDocs, showManualVetting
  ].filter(Boolean).length;

  return (
    <div className="space-y-4" data-testid="provider-search">
      {/* Filtri Primari */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3">
            {/* Search Text */}
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Cerca per nome, servizi, descrizione..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchProviders()}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            {/* Category */}
            <Select value={filterCategory || "_all"} onValueChange={(v) => setFilterCategory(v === "_all" ? "" : v)}>
              <SelectTrigger className="w-[150px]" data-testid="filter-category">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Tutte</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    <span className="flex items-center gap-2">
                      {cat.label}
                      {cat.sensitive && <Shield className="h-3 w-3 text-red-500" />}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Status */}
            <Select value={filterStatus || "_all"} onValueChange={(v) => setFilterStatus(v === "_all" ? "" : v)}>
              <SelectTrigger className="w-[160px]" data-testid="filter-status">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Tutti gli stati</SelectItem>
                {Object.entries(ADMIN_STATUS_CONFIG).map(([key, cfg]) => (
                  <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Advanced Toggle */}
            <Button
              variant={showAdvanced ? "default" : "outline"}
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="gap-2"
            >
              <Filter className="h-4 w-4" />
              Avanzati
              {activeFiltersCount > 0 && (
                <Badge className="bg-white text-umbria-olive ml-1">{activeFiltersCount}</Badge>
              )}
              {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            
            {/* Search Button */}
            <Button onClick={searchProviders} disabled={loading}>
              {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>

          {/* Filtri Avanzati (collassabili) */}
          {showAdvanced && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex flex-wrap gap-3 mb-3">
                {/* Risk Level */}
                <Select value={filterRisk || "_all"} onValueChange={(v) => setFilterRisk(v === "_all" ? "" : v)}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Rischio" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Tutti</SelectItem>
                    <SelectItem value="low">🟢 Basso</SelectItem>
                    <SelectItem value="medium">🟡 Medio</SelectItem>
                    <SelectItem value="high">🔴 Alto</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Source */}
                <Select value={filterSource || "_all"} onValueChange={(v) => setFilterSource(v === "_all" ? "" : v)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Fonte" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Tutte</SelectItem>
                    <SelectItem value="provider_portal">Portale</SelectItem>
                    <SelectItem value="admin_manual">Admin</SelectItem>
                    <SelectItem value="google_places">Google</SelectItem>
                    <SelectItem value="import_curated">Import</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Onboarding Method */}
                <Select value={filterOnboardingMethod || "_all"} onValueChange={(v) => setFilterOnboardingMethod(v === "_all" ? "" : v)}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Onboarding" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Tutti</SelectItem>
                    <SelectItem value="chat">💬 Chat Guidata</SelectItem>
                    <SelectItem value="form">📝 Form Classico</SelectItem>
                    <SelectItem value="skipped">⏭️ Saltato</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Macro Area */}
                <Select value={filterMacroArea || "_all"} onValueChange={(v) => setFilterMacroArea(v === "_all" ? "" : v)}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Macro Area" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Tutte</SelectItem>
                    {MACRO_AREAS.map(area => (
                      <SelectItem key={area} value={area}>{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Checkbox Filters */}
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox 
                    checked={showChiaraOnly}
                    onCheckedChange={setShowChiaraOnly}
                  />
                  <Eye className="h-4 w-4 text-green-600" />
                  Solo visibili a Chiara
                </label>
                
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox 
                    checked={showSensitiveOnly}
                    onCheckedChange={setShowSensitiveOnly}
                  />
                  <Shield className="h-4 w-4 text-red-600" />
                  Solo categorie sensibili
                </label>
                
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox 
                    checked={showMissingDocs}
                    onCheckedChange={setShowMissingDocs}
                  />
                  <FileText className="h-4 w-4 text-amber-600" />
                  Con documenti mancanti
                </label>
                
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox 
                    checked={showManualVetting}
                    onCheckedChange={setShowManualVetting}
                  />
                  <Users className="h-4 w-4 text-purple-600" />
                  Richiede vetting manuale
                </label>
                
                {activeFiltersCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500">
                    <X className="h-4 w-4 mr-1" />
                    Pulisci filtri
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between px-1">
        <p className="text-sm text-gray-500">
          {totalCount} provider trovati
        </p>
        {loading && <RefreshCw className="h-4 w-4 animate-spin text-gray-400" />}
      </div>

      {/* Results List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {providers.map((provider) => {
          const statusConfig = ADMIN_STATUS_CONFIG[provider.admin_status] || ADMIN_STATUS_CONFIG.registered;
          const riskConfig = RISK_CONFIG[provider.risk_level] || RISK_CONFIG.medium;
          const sourceConfig = SOURCE_CONFIG[provider.source] || SOURCE_CONFIG.provider_portal;
          const onboardingConfig = ONBOARDING_METHOD_CONFIG[provider.onboarding_method] || ONBOARDING_METHOD_CONFIG.unknown;
          const StatusIcon = statusConfig.icon;
          const isSensitive = provider.sensitive_category || provider.risk_level === 'high';
          
          return (
            <Card 
              key={provider.id}
              className={`cursor-pointer hover:shadow-md transition-all ${
                provider.visible_to_chiara 
                  ? 'border-l-4 border-l-green-500' 
                  : isSensitive 
                    ? 'border-l-4 border-l-red-400' 
                    : ''
              }`}
              onClick={() => onSelectProvider?.(provider.id)}
              data-testid={`provider-card-${provider.id}`}
            >
              <CardContent className="pt-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-medium text-umbria-charcoal truncate">
                        {provider.business_name}
                      </h3>
                      {provider.visible_to_chiara && (
                        <Eye className="h-4 w-4 text-green-500 flex-shrink-0" />
                      )}
                      {isSensitive && (
                        <Shield className="h-4 w-4 text-red-500 flex-shrink-0" />
                      )}
                    </div>
                    
                    {/* Badges Row 1: Status + Risk */}
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      <Badge className={statusConfig.color} variant="secondary">
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {statusConfig.label}
                      </Badge>
                      <Badge className={riskConfig.color} variant="secondary">
                        {riskConfig.label}
                      </Badge>
                    </div>
                    
                    {/* Badges Row 2: Source + Onboarding Method */}
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      <Badge className={sourceConfig.color} variant="secondary">
                        {sourceConfig.label}
                      </Badge>
                      {provider.source === 'provider_portal' && (
                        <Badge className={onboardingConfig.color} variant="secondary">
                          {onboardingConfig.icon} {onboardingConfig.label}
                        </Badge>
                      )}
                    </div>
                    
                    {/* Location */}
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {provider.type} • {provider.comune || provider.macro_area || 'Umbria'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {providers.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-500">
          <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>Nessun provider trovato</p>
          <p className="text-sm">Prova a modificare i filtri di ricerca</p>
        </div>
      )}
    </div>
  );
}
